import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import Modal from './Modal';
import Button from './Button';

// Modal de ajuste de enquadramento. O usuário arrasta + dá zoom pra escolher
// a área visível antes do upload. Saída: File JPEG cropado, no máximo 2000px
// no maior lado (mantém qualidade boa e fica abaixo do limite de 5MB).
//
// Props:
//   - file: File original escolhido pelo usuário (obrigatório quando open=true).
//   - aspect: razão (default 1 = quadrado, casa com aspect-square dos grids).
//   - maxDimension: maior lado do output em pixels (default 2000).
//   - quality: qualidade JPEG 0..1 (default 0.9).
//   - onConfirm(croppedFile): chamado com o File JPEG resultante.
//   - onCancel(): chamado quando o usuário fecha sem confirmar.

const MIME_OUT = 'image/jpeg';

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Falha ao ler imagem'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });
}

async function cropToFile(imageSrc, croppedAreaPixels, { maxDimension, quality, baseName }) {
  const image = await loadImage(imageSrc);
  const { x, y, width, height } = croppedAreaPixels;

  // Reduz a saída se o crop for maior que o limite, mantendo proporção.
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const outW = Math.round(width * scale);
  const outH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  // Quando o user dá zoom-out além da imagem (com restrictPosition=false), o
  // crop pode incluir região "fora" da imagem. JPEG não suporta transparência,
  // então preenchemos com branco antes — fica letterbox limpo em vez de preto.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(image, x, y, width, height, 0, 0, outW, outH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Falha ao gerar imagem cortada'))),
      MIME_OUT,
      quality
    );
  });

  const outName = (baseName || 'image').replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], outName, { type: MIME_OUT });
}

const ImageCropModal = ({
  file,
  open,
  aspect = 1,
  maxDimension = 2000,
  quality = 0.9,
  title = 'Ajustar imagem',
  onConfirm,
  onCancel,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !file) {
      setImageSrc(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    readAsDataUrl(file)
      .then((src) => { if (!cancelled) setImageSrc(src); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [open, file]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setProcessing(true);
    setError(null);
    try {
      const out = await cropToFile(imageSrc, croppedAreaPixels, {
        maxDimension,
        quality,
        baseName: file?.name,
      });
      onConfirm(out);
    } catch (err) {
      setError(err.message || 'Não foi possível processar a imagem.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={processing ? () => {} : onCancel}
      title={title}
      size="xl"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="subtle" onClick={onCancel} disabled={processing}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} loading={processing} disabled={!croppedAreaPixels}>
            Usar imagem
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 p-3 bg-error/10 border border-error/30 text-error rounded-lg text-sm">
          {error}
        </div>
      )}

      <div
        className="relative w-full bg-app-surface-hover rounded-lg overflow-hidden"
        style={{ height: 'min(65vh, 560px)' }}
      >
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.4}
            maxZoom={4}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            restrictPosition={false}
          />
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <label className="text-sm text-app-text-secondary w-12 shrink-0">Zoom</label>
        <input
          type="range"
          min={0.4}
          max={4}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1"
          aria-label="Ajustar zoom"
        />
      </div>

      <p className="mt-2 text-xs text-app-text-secondary">
        Arraste pra reposicionar e use o slider pra dar zoom. Pra incluir a
        imagem inteira, diminua o zoom até ela caber dentro do quadro.
      </p>
    </Modal>
  );
};

export default ImageCropModal;
